// src/pages/offlineSales/CashierSessionPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Plus, X, XCircle, RefreshCw } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import {
  getSessions, openSession,
} from '../../services/api/offlineSales/cashierSessions';
import { getShops } from '../../services/api/shops';
import { getUsers } from '../../services/api/users';
import { formatDateTime } from '../../utils/dateUtils';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import ZReportModal from './components/ZReportModal';
import CloseSessionModal from './components/CloseSessionModal';
import styles from './CashierSessionPage.module.css';
import sharedStyles from './components/OfflineSalesSharedModals.module.css';

// ─── Create Session Modal ─────────────────────────────────────────────────────
const CreateSessionModal = ({ shops, users, onCreated, onClose }) => {
  const { t } = useLocalization();
  const { error, success } = useAlert();
  const [shopId, setShopId] = useState('');
  const [cashierId, setCashierId] = useState('');
  const [openingCash, setOpeningCash] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shopId) { error('Please select a shop'); return; }
    if (!cashierId) { error('Please select a cashier'); return; }
    try {
      setLoading(true);
      const result = await openSession({
        shopId: parseInt(shopId),
        cashierId: parseInt(cashierId),
        openingCash: parseFloat(openingCash) || 0,
      });
      success('Session opened');
      onCreated(result.session);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to open session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={sharedStyles.overlay} onClick={onClose}>
      <div className={`${sharedStyles.modal} ${sharedStyles.modalSm}`} onClick={e => e.stopPropagation()}>
        <div className={sharedStyles.header}>
          <h3>{t('offlineSales.openNewSession')}</h3>
          <button onClick={onClose} className={sharedStyles.closeBtn}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div className={sharedStyles.body}>
            <div className={sharedStyles.formGroup}>
              <label className={sharedStyles.label}>{t('common.shop')} *</label>
              <Dropdown
                value={shopId}
                onChange={value => setShopId(value || '')}
                options={shops.map(s => ({ value: s.id, label: s.name }))}
                placeholder="Select shop..."
                searchable
              />
            </div>
            <div className={sharedStyles.formGroup}>
              <label className={sharedStyles.label}>{t('offlineSales.selectCashier')} *</label>
              <Dropdown
                value={cashierId}
                onChange={value => setCashierId(value || '')}
                options={users.map(u => ({ value: u.id, label: u.name }))}
                placeholder="Select cashier..."
                searchable
              />
            </div>
            <div className={sharedStyles.formGroup}>
              <label className={sharedStyles.label}>{t('offlineSales.openingCash')}</label>
              <input
                type="number" min="0" step="0.01"
                value={openingCash}
                onChange={e => setOpeningCash(e.target.value)}
                placeholder="0.00"
                className={sharedStyles.input}
              />
            </div>
          </div>
          <div className={sharedStyles.footer}>
            <button type="button" onClick={onClose} className={sharedStyles.cancelBtn}>{t('common.cancel')}</button>
            <button type="submit" disabled={loading} className={sharedStyles.confirmBtn}>
              {loading ? '...' : t('offlineSales.openSession')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const CashierSessionPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { error } = useAlert();

  const [sessions, setSessions] = useState([]);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ shopId: '', status: '' });
  const [showFilters, setShowFilters] = useState(true);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [closingSession, setClosingSession] = useState(null);
  const [zReportSession, setZReportSession] = useState(null);

  // Load shops and users for modal selectors
  useEffect(() => {
    const init = async () => {
      try {
        const [shopsData, usersData] = await Promise.all([
          getShops({ type: 'pos', limit: 100 }),
          getUsers({ limit: 100, isActive: true }),
        ]);
        setShops(shopsData.shops || shopsData || []);
        setUsers(usersData.users || usersData || []);
      } catch {
        error('Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')),
      };
      const result = await getSessions(params);
      setSessions(result.sessions || []);
      setPagination(p => ({ ...p, total: result.total || 0 }));
    } catch {
      error('Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, filters, error]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleCreated = () => {
    setShowCreateModal(false);
    loadSessions();
  };

  const handleClosed = () => {
    setClosingSession(null);
    loadSessions();
  };

  const filterConfig = [
    {
      key: 'shopId',
      type: 'select',
      label: t('offlineSales.filterByShop'),
      options: [
        { value: '', label: 'All Shops' },
        ...shops.map(s => ({ value: String(s.id), label: s.name })),
      ],
    },
    {
      key: 'status',
      type: 'select',
      label: t('offlineSales.filterByStatus'),
      options: [
        { value: '', label: 'All' },
        { value: 'open', label: t('offlineSales.statusOpen') },
        { value: 'closed', label: t('offlineSales.statusClosed') },
      ],
    },
  ];

  const columns = [
    { key: 'id', header: '#', width: '60px', render: row => `#${row.id}` },
    { key: 'shop', header: t('common.shop'), render: row => row.Shop?.name || '—' },
    { key: 'cashier', header: t('offlineSales.cashierCol'), render: row => row.cashier?.name || '—' },
    {
      key: 'status', header: t('offlineSales.statusCol'), width: '90px',
      render: row => (
        <span className={`${styles.badge} ${row.status === 'open' ? styles.badgeOpen : styles.badgeClosed}`}>
          {row.status === 'open' ? t('offlineSales.sessionOpen') : t('offlineSales.sessionClosed')}
        </span>
      ),
    },
    { key: 'openedAt', header: t('offlineSales.openedAt'), render: row => formatDateTime(row.openedAt) },
    { key: 'closedAt', header: t('offlineSales.closedAt'), render: row => row.closedAt ? formatDateTime(row.closedAt) : '—' },
    { key: 'openingCash', header: t('offlineSales.openingCash'), align: 'right', render: row => formatCurrency(row.openingCash || 0) },
    { key: 'closingCash', header: t('offlineSales.closingCash'), align: 'right', render: row => row.closingCash != null ? formatCurrency(row.closingCash) : '—' },
  ];

  const renderActions = (row) => (
    <div style={{ display: 'flex', gap: 6 }}>
      {row.status === 'open' && (
        <button
          className={styles.closeBtn}
          onClick={() => setClosingSession(row)}
          title={t('offlineSales.closeSession')}
        >
          <XCircle size={13} /> {t('offlineSales.closeSession')}
        </button>
      )}
      <button
        className={styles.reportBtn}
        onClick={() => setZReportSession(row)}
        title={t('offlineSales.viewZReport')}
      >
        {t('offlineSales.viewZReport')}
      </button>
    </div>
  );

  if (loading) {
    return <div className={styles.loading}><div className={styles.spinner} /></div>;
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.cashierSessions')}
        icon={Clock}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={styles.refreshBtn} onClick={loadSessions} title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button className={styles.primaryBtn} onClick={() => setShowCreateModal(true)}>
              <Plus size={16} />
              {t('offlineSales.createSession')}
            </button>
          </div>
        }
      />

      <FilterPanel
        filters={filterConfig}
        values={filters}
        onChange={setFilters}
        show={showFilters}
        onToggle={() => setShowFilters(v => !v)}
      />

      <DataTable
        data={sessions}
        columns={columns}
        actions={{ render: renderActions, width: '220px' }}
        loading={sessionsLoading}
        emptyMessage={t('offlineSales.cashierSessions')}
        emptyDescription="No sessions found"
        pagination={{ ...pagination, pageSizeOptions: [10, 20, 50] }}
        onPaginationChange={(changes) => setPagination(p => ({ ...p, ...changes }))}
      />

      {showCreateModal && (
        <CreateSessionModal
          shops={shops}
          users={users}
          onCreated={handleCreated}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {closingSession && (
        <CloseSessionModal
          session={closingSession}
          onClosed={handleClosed}
          onClose={() => setClosingSession(null)}
        />
      )}

      {zReportSession && (
        <ZReportModal
          session={zReportSession}
          onClose={() => setZReportSession(null)}
        />
      )}
    </div>
  );
};

export default CashierSessionPage;
